import React, { Component, useRef, useEffect, useCallback } from 'react';
import { DndContext, useDragLayer } from 'react-dnd';
import throttle from 'lodash.throttle';
import raf from 'raf';

const DEFAULT_BUFFER = 150;

function noop() {}

function intBetween(min, max, val) {
  return Math.floor(
    Math.min(max, Math.max(min, val))
  );
}

function getCoords(evt) {
  if (evt.type === 'touchmove') {
    return { x: evt.changedTouches[0].clientX, y: evt.changedTouches[0].clientY };
  }

  return { x: evt.clientX, y: evt.clientY };
}

function createHorizontalStrength(_buffer) {
  return function defaultHorizontalStrength({ x, w, y, h }, point) {
    const buffer = Math.min(w / 2, _buffer);
    const inRange = point.x >= x && point.x <= x + w;
    const inBox = inRange && point.y >= y && point.y <= y + h;

    if (inBox) {
      if (point.x < x + buffer) {
        return (point.x - x - buffer) / buffer;
      } else if (point.x > (x + w - buffer)) {
        return -(x + w - point.x - buffer) / buffer;
      }
    }

    return 0;
  };
}

function createVerticalStrength(_buffer) {
  return function defaultVerticalStrength({ y, h, x, w }, point) {
    const buffer = Math.min(h / 2, _buffer);
    const inRange = point.y >= y && point.y <= y + h;
    const inBox = inRange && point.x >= x && point.x <= x + w;

    if (inBox) {
      if (point.y < y + buffer) {
        return (point.y - y - buffer) / buffer;
      } else if (point.y > (y + h - buffer)) {
        return -(y + h - point.y - buffer) / buffer;
      }
    }
    return 0;
  };
}

const defaultHorizontalStrength = createHorizontalStrength(DEFAULT_BUFFER);
const defaultVerticalStrength = createVerticalStrength(DEFAULT_BUFFER);

export default function createScrollingComponent(WrappedComponent, currentRef = null) {
  const ScrollZoneHOC = ({ onScrollChange = noop, horizontalStrength = defaultHorizontalStrength, verticalStrength = defaultVerticalStrength, strengthMultiplier = 10, ...props}) => {
    const scaleXRef = useRef(0);
    const scaleYRef = useRef(0);
    const attached = useRef(false);
    const frame = useRef(null);

    const {isDragging} = useDragLayer(monitor => ({
      isDragging: monitor.isDragging()
    }));

    const detach = useCallback(() => {
      attached.current = false;
      window.document.body.removeEventListener('dragover', updateScrolling);
      window.document.body.removeEventListener('touchmove', updateScrolling);
    }, []);

    const stopScrolling = useCallback(() => {
      detach();
      scaleXRef.current = 0;
      scaleYRef.current = 0;

      if (frame.current) {
        raf.cancel(frame.current);
        frame.current = null;
      }
    }, []);

    const startScrolling = useCallback(() => {
      let i = 0;
      const tick = () => {
        // stop scrolling if there's nothing to do
        if (strengthMultiplier === 0 || scaleXRef.current + scaleYRef.current === 0) {
          stopScrolling();
          return;
        }

        // there's a bug in safari where it seems like we can't get
        // mousemove events from a container that also emits a scroll
        // event that same frame. So we double the strengthMultiplier and only adjust
        // the scroll position at 30fps
        if (i++ % 2) {
          const {
            scrollLeft,
            scrollTop,
            scrollWidth,
            scrollHeight,
            clientWidth,
            clientHeight,
          } = currentRef.current;

          const newLeft = scaleXRef.current
            ? currentRef.current.scrollLeft = intBetween(
              0,
              scrollWidth - clientWidth,
              scrollLeft + scaleXRef.current * strengthMultiplier
            )
            : scrollLeft;

          const newTop = scaleYRef.current
            ? currentRef.current.scrollTop = intBetween(
              0,
              scrollHeight - clientHeight,
              scrollTop + scaleYRef.current * strengthMultiplier
            )
            : scrollTop;
          onScrollChange(newLeft, newTop);
        }
        frame.current = raf(tick);
      };

      tick();
    }, []);

    // Update scaleX and scaleY every 100ms or so
    // and start scrolling if necessary
    const updateScrolling = useCallback(throttle(evt => {
      const { left: x, top: y, width: w, height: h } = currentRef.current.getBoundingClientRect();
      const box = { x, y, w, h };
      const coords = getCoords(evt);

      // calculate strength
      scaleXRef.current = horizontalStrength(box, coords);
      scaleYRef.current = verticalStrength(box, coords);

      // start scrolling if we need to
      if (!frame.current && (scaleXRef.current || scaleYRef.current)) {
        startScrolling();
      }
    }, 100, { trailing: false }), []);

    const attach = useCallback(() => {
      attached.current = true;
      window.document.body.addEventListener('dragover', updateScrolling);
      window.document.body.addEventListener('touchmove', updateScrolling);
    }, []);

    const handleEvent = useCallback((evt) => {
      if (isDragging && !attached.current) {
        attach();
        updateScrolling(evt);
      }
    }, [isDragging])

    useEffect(() => {
      currentRef.current.addEventListener('dragover', handleEvent);
      // touchmove events don't seem to work across siblings, so we unfortunately
      // have to attach the listeners to the body
      window.document.body.addEventListener('touchmove', handleEvent);
      return () => {
        currentRef.current.removeEventListener('dragover', handleEvent);
        window.document.body.removeEventListener('touchmove', handleEvent);
        stopScrolling();
      }
    }, [handleEvent]);

    useEffect(() => {
      if (isDragging) {
      } else {
        stopScrolling();
      }
    }, [isDragging]);

    return (
      <WrappedComponent
        ref={currentRef}
        {...props}
      />
    );
  }

  class ScrollingComponent extends Component {

    static displayName = `Scrolling()`;

    static defaultProps = {
      onScrollChange: noop,
      verticalStrength: defaultVerticalStrength,
      horizontalStrength: defaultHorizontalStrength,
      strengthMultiplier: 10,
    };

    wrappedInstance = currentRef || React.createRef();

    static contextType = DndContext;

    constructor(props, ctx) {
      super(props, ctx);

      this.scaleX = 0;
      this.scaleY = 0;
      this.frame = null;

      this.attached = false;
      this.dragging = false;
    }

    componentDidMount() {
      this.container = this.wrappedInstance.current;
      this.container.addEventListener('dragover', this.handleEvent);
      // touchmove events don't seem to work across siblings, so we unfortunately
      // have to attach the listeners to the body
      window.document.body.addEventListener('touchmove', this.handleEvent);

      this.clearMonitorSubscription = this.context
          .dragDropManager
          .getMonitor()
          .subscribeToStateChange(() => this.handleMonitorChange());
    }

    componentWillUnmount() {
      this.container.removeEventListener('dragover', this.handleEvent);
      window.document.body.removeEventListener('touchmove', this.handleEvent);
      if (this.clearMonitorSubscription) {
        this.clearMonitorSubscription();
      }
      this.stopScrolling();
    }

    handleEvent = (evt) => {
      if (this.dragging && !this.attached) {
        this.attach();
        this.updateScrolling(evt);
      }
    }

    handleMonitorChange() {
      const isDragging = this.context.dragDropManager.getMonitor().isDragging();

      if (!this.dragging && isDragging) {
        this.dragging = true;
      } else if (this.dragging && !isDragging) {
        this.dragging = false;
        this.stopScrolling();
      }
    }

    attach() {
      this.attached = true;
      window.document.body.addEventListener('dragover', this.updateScrolling);
      window.document.body.addEventListener('touchmove', this.updateScrolling);
    }

    detach() {
      this.attached = false;
      window.document.body.removeEventListener('dragover', this.updateScrolling);
      window.document.body.removeEventListener('touchmove', this.updateScrolling);
    }

    // Update scaleX and scaleY every 100ms or so
    // and start scrolling if necessary
    updateScrolling = throttle(evt => {
      const { left: x, top: y, width: w, height: h } = this.container.getBoundingClientRect();
      const box = { x, y, w, h };
      const coords = getCoords(evt);

      // calculate strength
      this.scaleX = this.props.horizontalStrength(box, coords);
      this.scaleY = this.props.verticalStrength(box, coords);

      // start scrolling if we need to
      if (!this.frame && (this.scaleX || this.scaleY)) {
        this.startScrolling();
      }
    }, 100, { trailing: false })

    startScrolling() {
      let i = 0;
      const tick = () => {
        const { scaleX, scaleY, container } = this;
        const { strengthMultiplier, onScrollChange } = this.props;

        // stop scrolling if there's nothing to do
        if (strengthMultiplier === 0 || scaleX + scaleY === 0) {
          this.stopScrolling();
          return;
        }

        // there's a bug in safari where it seems like we can't get
        // mousemove events from a container that also emits a scroll
        // event that same frame. So we double the strengthMultiplier and only adjust
        // the scroll position at 30fps
        if (i++ % 2) {
          const {
            scrollLeft,
            scrollTop,
            scrollWidth,
            scrollHeight,
            clientWidth,
            clientHeight,
          } = container;

          const newLeft = scaleX
            ? container.scrollLeft = intBetween(
              0,
              scrollWidth - clientWidth,
              scrollLeft + scaleX * strengthMultiplier
            )
            : scrollLeft;

          const newTop = scaleY
            ? container.scrollTop = intBetween(
              0,
              scrollHeight - clientHeight,
              scrollTop + scaleY * strengthMultiplier
            )
            : scrollTop;
          onScrollChange(newLeft, newTop);
        }
        this.frame = raf(tick);
      };

      tick();
    }

    stopScrolling() {
      this.detach();
      this.scaleX = 0;
      this.scaleY = 0;

      if (this.frame) {
        raf.cancel(this.frame);
        this.frame = null;
      }
    }

    render() {
      const {
        // not passing down these props
        strengthMultiplier,
        verticalStrength,
        horizontalStrength,
        onScrollChange,

        ...props
      } = this.props;

      return (
        <WrappedComponent
          ref={this.wrappedInstance}
          {...props}
        />
      );
    }
  }
  return ScrollZoneHOC;
}